import { Injectable, OnDestroy } from '@angular/core';
import { Observable, Subject, of } from 'rxjs';
import { CommunicationsApiService } from '../store/communications/communications-api.service';
import { catchError, map, take } from 'rxjs/operators';
import { FieldDataType, QueryParams } from '../../core/store/models';
import { Dataset } from '../../core/store/models/dataset.model';
import { CultureService } from '../../core/services/culture.service';
import { UserProfile } from '../../core/store/user-profile';
import { DEFAULT_APP_CULTURE_CODE } from '../../core/languages/language.model';
import { TextValueByCulture } from '../../core/store/models/translations.model';
import { FieldToValueMapping } from '../components/template/designer/design-elements/design-elements.models';
import { Document, NumberFormat, DateFieldFormat } from 'src/app/modules/core/store/models/document.model';
import { ChannelUIds, CommunicationTemplate, MassNotificationTemplate } from '../store/communications/communications.model';
import { BehaviorSubject } from 'rxjs';
import { UserContextService } from '@app/modules/core/services/user-context.service';
import { Permissions } from '@app/modules/authentication/store/auth';

@Injectable({
  providedIn: 'root'
})
export class TemplateDesignerService {
  constructor(
    private communicationsApiService: CommunicationsApiService,
    private cultureService: CultureService,
    private userContext: UserContextService
  ) {}

  staticTextTranslations: any;
  loadingTranslations: boolean = false;
  datasets: Dataset[];
  userProfile: UserProfile;

  public init(editMode: boolean) {
    if (!this.staticTextTranslations && !this.loadingTranslations) {
      this.loadStaticTextTranslations();
    }

    if (editMode === true) {
      this.loadDatasets();
    }
    this.userProfile = this.cultureService.getUserProfile();
  }

  public get hasTranslations(): boolean {
    return !!this.staticTextTranslations || this.loadingTranslations === true;
  }

  public initDatasets(forceLost?: boolean): void {
    if (!!!this.datasets || forceLost === true) {
      this.loadDatasets();
    }
  }

  getCommunicationTemplateByChannelUId(template: MassNotificationTemplate, channelUId: string): CommunicationTemplate {
    if (channelUId === ChannelUIds.SendGridEmailChannelUId || channelUId === ChannelUIds.TopoEmailChannelUId) {
      return template?.communicationTemplates?.find(
        t => t.channelUId === ChannelUIds.SendGridEmailChannelUId || t.channelUId === ChannelUIds.TopoEmailChannelUId
      );
    } else if (channelUId === ChannelUIds.TwilioChannelUId || channelUId === ChannelUIds.HorizonSmsChannelUId) {
      return template?.communicationTemplates?.find(
        t => t.channelUId === ChannelUIds.TwilioChannelUId || t.channelUId === ChannelUIds.HorizonSmsChannelUId
      );
    } else {
      return template?.communicationTemplates?.find(t => t.channelUId === channelUId);
    }

    return null;
  }

  getTranslateText(translations: TextValueByCulture, culture: string): string {
    if (!!!translations) {
      return null;
    }
    let text = translations[culture];
    if (!!!text) {
      var fallBackCultureCode = this.userProfile?.culture?.fallbackCultureCode ?? DEFAULT_APP_CULTURE_CODE;
      text = translations[fallBackCultureCode];
      if (!!!text && fallBackCultureCode !== DEFAULT_APP_CULTURE_CODE) {
        text = translations[DEFAULT_APP_CULTURE_CODE];
      }
    }
    return text;
  }

  getStaticTranslateText(key: string, culture: string): string {
    if (!!this.staticTextTranslations?.[key][culture]) {
      return this.staticTextTranslations[key][culture];
    }
    return null;
  }

  buildDocumentFieldMapping(dataset: Dataset, document: Document, culture: string): FieldToValueMapping {
    const results: FieldToValueMapping = {};

    if (!!document) {
      Object.keys(document).forEach(key => {
        const value = document[key];
        let result = { value: value, format: null };
        const field = dataset?.schema?.fields.find(f => f.systemName === key);
        const dataType = field?.dataType;

        if (dataType === FieldDataType.Text) {
          if (!!field) {
            result.format = field.settings?.format;
          }
        } else if (dataType === FieldDataType.DateTime) {
          result.value = this.formatDate(value, culture, field.settings?.format);
        } else if (dataType == FieldDataType.Number) {
          result.value = this.formatNumber(value, culture, field.settings?.format);
        } else if (dataType == FieldDataType.Picklist) {
          if (!!field) {
            result.value = field.picklistValues?.find(v => v.uId === value)?.value;
          }
        } else if (dataType == FieldDataType.MultiPicklist) {
          if (!!field && !!value) {
            let displayVal = '';
            for (const uid of value) {
              displayVal += `${field.picklistValues?.find(p => p.uId === uid)?.value}, `;
            }
            if (displayVal.length > 0) {
              displayVal = displayVal.slice(0, -2);
            }
            result.value = displayVal;
          }
        } else if (dataType == FieldDataType.UId && field?.systemName === 'workflow_uid') {
          result.value = dataset?.workflow?.name;
        } else if (dataType == FieldDataType.UId && field?.systemName === 'workflow_step_uid') {
          result.value = dataset?.workflow?.workflowSteps?.find(s => s.uId === value)?.statusName;
        } else if (dataType == FieldDataType.User) {
          result.value = document[key + '_name'];
        }
        results[key] = result;
      });
    }
    return results;
  }

  formatDate(value: any, culture: string, format?: any): string {
    if (!value) {
      return '';
    }

    let options = {} as Intl.DateTimeFormatOptions;

    if (format !== DateFieldFormat.DateOnly) {
      options.hour = 'numeric';
      options.minute = 'numeric';
    }
    if (format === DateFieldFormat.LongDateWithTime24Hr || format === DateFieldFormat.TimeOnly24Hr) {
      options.hour12 = false;
    } else {
      options.hour12 = true;
    }
    if (format !== DateFieldFormat.TimeOnly && format !== DateFieldFormat.TimeOnly24Hr) {
      options.year = 'numeric';
      options.month = 'numeric';
      options.day = 'numeric';
    }

    return new Intl.DateTimeFormat(culture, options).format(new Date(value)).replace(',', '');
  }

  formatNumber(value: any, culture: string, format?: any): string {
    if (!value) {
      value = 0;
    }
    let options = {
      notation: 'standard',
      style: 'decimal'
    } as Intl.NumberFormatOptions;

    if (format === NumberFormat.Number || format === undefined) {
      options.maximumFractionDigits = 0;
    } else {
      options.minimumFractionDigits = 2;
      options.maximumFractionDigits = 2;
    }
    if (format === NumberFormat.Accounting) {
      options.useGrouping = true;
    }

    return new Intl.NumberFormat(culture, options).format(value);
  }

  getNumberOfContactFields(datasetUId: string): number {
    const dataset = this.datasets?.find(d => d.uId === datasetUId);

    const hasContactField = (dataset?.schema?.fields ?? []).some(({ dataType, settings }) => {
      return (dataType === FieldDataType.Email || dataType === FieldDataType.Phone) && settings?.useForCommunications !== false;
    });

    return hasContactField ? 1 : 0

    // ** NOTE **
    // leaving previous code commented out just in case useful to know about in the future
    // previously, contact fields were multiplied by number of fields of type that has the most
    // the purpose of this logic is unknown.
    // ** ---- **
    
    // if (!!dataset) {
    //   const emailFields = dataset.schema.fields.filter(
    //     f => f.dataType === FieldDataType.Email && f.settings?.useForCommunications !== false
    //   );
    //   const phoneFields = dataset.schema.fields.filter(
    //     f => f.dataType === FieldDataType.Phone && f.settings?.useForCommunications !== false
    //   );
    //   return emailFields.length > phoneFields.length ? emailFields.length : phoneFields.length;
    // }
    // return 0;
  }

  private loadStaticTextTranslations(): void {
    this.loadingTranslations = true;
    this.getStaticTextTranslations()
      .pipe(take(1))
      .subscribe(data => {
        this.staticTextTranslations = data;
        this.loadingTranslations = false;
      });
  }

  private getStaticTextTranslations(): Observable<any> {
    const userContext = this.userContext.getUserContext();

    if (!userContext.hasPermission(Permissions.CommunicationTemplateRead)) {
      return of([]);
    }

    return this.communicationsApiService.getStaticTextTranslations().pipe(
      catchError(() => of({ data: [] })),
      map(data => {
        return data;
      })
    );
  }

  private loadDatasets(): void {
    this.getDatasets()
      .pipe(take(1))
      .subscribe(data => {
        this.datasets = data;
      });
  }

  private getDatasets(): Observable<Dataset[]> {
    const queryParams: QueryParams = {
      skip: 0,
      take: 1000,
      sortField: 'Name',
      sortDirection: 'Ascending',
      where: `IsActive==true`
    };

    return this.communicationsApiService.getDatasets(queryParams, false, true, true).pipe(
      catchError(() => of({ data: [] })),
      map(res => {
        const data = res?.data ?? [];
        return data;
      })
    );
  }

  // Updated Template Designer Service
  private smsCountSource = new BehaviorSubject<number>(0);
  public currentCount$ = this.smsCountSource.asObservable();
  readonly MAX_CHARS = 277;

  // Add public method to access current value
  getCurrentSmsCount(): number {
    return this.smsCountSource.value;
  }

  updateCount(count: number, isSms: boolean) {
    if (isSms) {
      this.smsCountSource.next(count);
    }
  }
}
